import { ChangeAttrName } from '@common/flare/change-attr-name.enum';
import { FlareMadCapXMLNSAttribute } from '@common/flare/constants/flare-madcap-xmlns.constant';
import { FlareXMLDeclaration } from '@common/flare/constants/flare-xml-declaration.constant';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { TrackedChangesIgnoreMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { TrackedChange } from '@common/prosemirror/tracked-changes/tracked-change.type';
import { getTrackedChangeIdsFromAttrValue, removeTrackedChangesFromNodeAttrs, toTrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-changes';
import { ReviewApplyOption } from '@common/reviews/enums/review-apply-option.enum';
import { ReviewFilesTrackedChangeCount } from '@common/reviews/models/review-files-tracked-change-count.model';
import { ReviewMergeOption } from '@common/reviews/types/review-merge-option.type';
import { Mark, Node as ProseMirrorNode } from 'prosemirror-model';
import { Transaction } from 'prosemirror-state';

/** Whether a tracked change is being finalized (`accept`) or undone (`reject`). */
export type TrackedChangeResolution = 'accept' | 'reject';

/**
 * Thrown when rejecting a `Bind` change. A bind change (eg. bolding text, wrapping it in a list) only records
 * that a change happened on the mark/node it decorates - it does not record which formatting mark or wrapper node
 * was added, so there is no "before" state to revert to. Accepting a bind change (keep the formatting, stop
 * tracking it) is always supported; only rejecting it is not.
 */
export class UnsupportedTrackedChangeResolutionError extends Error {
  constructor(changeType: ChangeType, resolution: TrackedChangeResolution) {
    super(`Cannot ${resolution} a '${changeType}' tracked change: no "before" state was recorded for it.`);
    this.name = 'UnsupportedTrackedChangeResolutionError';
  }
}

interface TrackedChangeTarget {
  /** The position of the node/text run in the doc snapshot passed to the resolve function, before any of this call's steps are applied. */
  pos: number;
  node: ProseMirrorNode;
  /** Set when the change lives on a mark (eg. text runs) rather than on the node's own attrs. */
  mark?: Mark;
  change: TrackedChange;
}

/**
 * Finds every node/mark instance in `doc` that carries a tracked change with the given id.
 * A single logical change can span multiple text node runs (when other marks split the run) so this can return more than one target.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc to search.
 * @param changeId The tracked change id to find.
 * @returns The targets that carry the change.
 */
function findTrackedChangeTargets(schema: CollabSchema, doc: ProseMirrorNode, changeId: string): TrackedChangeTarget[] {
  const targets: TrackedChangeTarget[] = [];

  doc.descendants((node, pos) => {
    if (node.isText || node.type.spec.unchangeable) {
      node.marks?.forEach(mark => {
        if (mark.type === schema.changeMark) {
          const change = toTrackedChangeNodeAttrs(mark.attrs)?.changeList?.find(candidate => candidate.id === changeId);
          if (change) {
            targets.push({ pos, node, mark, change });
          }
        }
      });
    } else {
      const change = toTrackedChangeNodeAttrs(node.attrs)?.changeList?.find(candidate => candidate.id === changeId);
      if (change) {
        targets.push({ pos, node, change });
      }
    }
  });

  return targets;
}

/** Removes a single change (by id) from a mark's change list, keeping any other changes on the same mark. */
function stripChangeFromMark(tr: Transaction, schema: CollabSchema, node: ProseMirrorNode, mark: Mark, pos: number, changeId: string) {
  const changeList = (toTrackedChangeNodeAttrs(mark.attrs)?.changeList ?? []).filter(change => change.id !== changeId);

  tr.removeMark(pos, pos + node.nodeSize, mark);

  if (changeList.length > 0) {
    tr.addMark(pos, pos + node.nodeSize, schema.changeMark.create({
      [ChangeAttrName.ChangeIds]: changeList.map(change => change.id).join(','),
      changeList
    }));
  }
}

/** Removes a single change (by id) from a node's change list, keeping any other changes on the same node. */
function stripChangeFromNode(tr: Transaction, node: ProseMirrorNode, pos: number, changeId: string) {
  const changeList = (toTrackedChangeNodeAttrs(node.attrs)?.changeList ?? []).filter(change => change.id !== changeId);

  const newAttrs = changeList.length > 0
    ? { ...node.attrs, [ChangeAttrName.ChangeIds]: changeList.map(change => change.id).join(','), changeList }
    : removeTrackedChangesFromNodeAttrs(node.attrs);

  tr.setNodeMarkup(pos, null, newAttrs);
}

/** Strips a target's change, keeping its content (used to finalize an add/replace/bind/unbind, or to undo a remove). */
function stripChange(tr: Transaction, schema: CollabSchema, target: TrackedChangeTarget, pos: number, changeId: string) {
  if (target.mark) {
    stripChangeFromMark(tr, schema, target.node, target.mark, pos, changeId);
  } else {
    stripChangeFromNode(tr, target.node, pos, changeId);
  }
}

/** Replaces a single change (by id) within a mark's change list with the result of `updateChange`, keeping any other changes on the same mark untouched. */
function replaceChangeInMark(tr: Transaction, schema: CollabSchema, node: ProseMirrorNode, mark: Mark, pos: number, changeId: string, updateChange: (change: TrackedChange) => TrackedChange) {
  const changeList = (toTrackedChangeNodeAttrs(mark.attrs)?.changeList ?? []).map(change => change.id === changeId ? updateChange(change) : change);

  tr.removeMark(pos, pos + node.nodeSize, mark);
  tr.addMark(pos, pos + node.nodeSize, schema.changeMark.create({
    [ChangeAttrName.ChangeIds]: changeList.map(change => change.id).join(','),
    changeList
  }));
}

/** Replaces a single change (by id) within a node's change list with the result of `updateChange`, keeping any other changes on the same node untouched. */
function replaceChangeInNode(tr: Transaction, node: ProseMirrorNode, pos: number, changeId: string, updateChange: (change: TrackedChange) => TrackedChange) {
  const changeList = (toTrackedChangeNodeAttrs(node.attrs)?.changeList ?? []).map(change => change.id === changeId ? updateChange(change) : change);

  tr.setNodeMarkup(pos, null, { ...node.attrs, [ChangeAttrName.ChangeIds]: changeList.map(change => change.id).join(','), changeList });
}

/**
 * Parses a node's serialized code (`TrackedChange.changeContent`) back into a node, for reverting a replace/unbind.
 * `changeContent` is stored as a bare XML fragment (the node's own tag, eg. `<table>...</table>`, same as what
 * `TrackedChangesImporter` reads out of `<MadCap:changeData>`) rather than a full Flare document, so it's wrapped
 * in a minimal doc/html/body shell before parsing and then unwrapped again. Inline content (a mark-level change,
 * eg. unbinding a `<b>` around text) additionally needs a `<p>` wrapper or the parser auto-wraps it in a
 * synthetic mcCentralContainer instead of leaving it as-is.
 * @param schema The schema to parse the content with.
 * @param changeContent The serialized node/fragment to reconstruct.
 * @param isInline Whether the content is inline (mark-level) rather than a standalone block node.
 */
function reconstructNodeFromChangeContent(schema: CollabSchema, changeContent: string, isInline: boolean): ProseMirrorNode {
  const bodyContent = isInline ? `<p>${changeContent}</p>` : changeContent;
  const wrappedDoc = schema.codeToDoc(`${FlareXMLDeclaration}<html ${FlareMadCapXMLNSAttribute}><body>${bodyContent}</body></html>`);
  const bodyFirstChild = wrappedDoc.content.firstChild.content.firstChild.content.firstChild;

  return isInline ? bodyFirstChild.content.firstChild : bodyFirstChild;
}

/**
 * Accepts or rejects a single tracked change (identified by its change id) within `doc`, applying the necessary steps to `tr`.
 * `doc` must be `tr.doc` as it was before any steps from this call were added - each call establishes its own mapping
 * checkpoint so callers can resolve several changes against the same transaction by re-passing `tr.doc` between calls
 * (see `acceptAllTrackedChanges`/`rejectAllTrackedChanges`).
 * @param tr The transaction to apply the resolution to.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc (equal to `tr.doc` before this call's steps) to search for the change.
 * @param changeId The id of the tracked change to resolve.
 * @param resolution Whether to accept or reject the change.
 * @returns `tr`, for chaining.
 */
export function resolveTrackedChange(tr: Transaction, schema: CollabSchema, doc: ProseMirrorNode, changeId: string, resolution: TrackedChangeResolution): Transaction {
  const targets = findTrackedChangeTargets(schema, doc, changeId);
  const mappingCheckpoint = tr.steps.length;

  targets.forEach(target => {
    const pos = tr.mapping.slice(mappingCheckpoint).map(target.pos);
    const node = tr.doc.nodeAt(pos) ?? target.node;
    const changeType = target.change.changeType;

    switch (changeType) {
      // Add/Remove are opposites: accepting an add (or rejecting a remove) keeps the content and stops tracking it;
      // rejecting an add (or accepting a remove) deletes the content outright.
      case ChangeType.Add:
      case ChangeType.Remove: {
        const keepContent = resolution === 'accept' ? changeType === ChangeType.Add : changeType === ChangeType.Remove;
        if (keepContent) {
          stripChange(tr, schema, target, pos, changeId);
        } else {
          tr.delete(pos, pos + node.nodeSize);
        }
        break;
      }

      // The node's current attrs already hold the new value; TrackedChange.value holds the old one.
      case ChangeType.Attributes:
        if (resolution === 'accept') {
          stripChangeFromNode(tr, node, pos, changeId);
        } else {
          tr.setNodeMarkup(pos, null, { ...node.attrs, [target.change.attribute]: target.change.value });
          stripChangeFromNode(tr, tr.doc.nodeAt(pos), pos, changeId);
        }
        break;

      // The node's current content already holds the new state; TrackedChange.changeContent holds the serialized old node.
      case ChangeType.Replace:
      case ChangeType.Unbind:
        if (resolution === 'accept') {
          stripChange(tr, schema, target, pos, changeId);
        } else {
          tr.replaceRangeWith(pos, pos + node.nodeSize, reconstructNodeFromChangeContent(schema, target.change.changeContent, !!target.mark));
        }
        break;

      // Bind (eg. bolding text) has no recorded "before" state - only accepting it (keep the formatting) is supported.
      case ChangeType.Bind:
        if (resolution === 'accept') {
          stripChange(tr, schema, target, pos, changeId);
        } else {
          throw new UnsupportedTrackedChangeResolutionError(changeType, resolution);
        }
        break;

      default:
        break;
    }
  });

  return tr;
}

/**
 * Accepts a single tracked change, keeping its resulting content and removing its change tracking.
 * @param tr The transaction to apply the acceptance to.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc (equal to `tr.doc` before this call's steps) to search for the change.
 * @param changeId The id of the tracked change to accept.
 * @returns `tr`, for chaining.
 */
export function acceptTrackedChange(tr: Transaction, schema: CollabSchema, doc: ProseMirrorNode, changeId: string): Transaction {
  return resolveTrackedChange(tr, schema, doc, changeId, 'accept');
}

/**
 * Rejects a single tracked change, reverting the doc to the state it was in before the change was made.
 * @param tr The transaction to apply the rejection to.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc (equal to `tr.doc` before this call's steps) to search for the change.
 * @param changeId The id of the tracked change to reject.
 * @returns `tr`, for chaining.
 */
export function rejectTrackedChange(tr: Transaction, schema: CollabSchema, doc: ProseMirrorNode, changeId: string): Transaction {
  return resolveTrackedChange(tr, schema, doc, changeId, 'reject');
}

/**
 * Marks (or clears) how a single tracked change should be resolved when the review package it belongs to is
 * later applied to the workspace, without resolving the change itself - the change stays tracked exactly as
 * it was, only its `reviewMergeOption` metadata changes. Used by the review gutter so a reviewer can flag
 * individual changes as `Accept`/`Reject` ahead of time (and change their mind later) without altering the
 * review doc's content. See `resolveTrackedChangesForReviewApply` for where these marks take effect.
 * @param tr The transaction to apply the mark to.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc (equal to `tr.doc` before this call's steps) to search for the change.
 * @param changeId The id of the tracked change to mark.
 * @param reviewMergeOption The option to mark the change with, or `undefined` to clear an existing mark.
 * @returns `tr`, for chaining.
 */
export function setTrackedChangeReviewMergeOption(tr: Transaction, schema: CollabSchema, doc: ProseMirrorNode, changeId: string, reviewMergeOption: ReviewMergeOption | undefined): Transaction {
  const targets = findTrackedChangeTargets(schema, doc, changeId);
  const mappingCheckpoint = tr.steps.length;

  const updateChange = (change: TrackedChange): TrackedChange => {
    if (reviewMergeOption === undefined) {
      const changeWithoutReviewMergeOption = { ...change };
      delete changeWithoutReviewMergeOption.reviewMergeOption;
      return changeWithoutReviewMergeOption;
    }

    return { ...change, reviewMergeOption };
  };

  targets.forEach(target => {
    const pos = tr.mapping.slice(mappingCheckpoint).map(target.pos);

    if (target.mark) {
      replaceChangeInMark(tr, schema, target.node, target.mark, pos, changeId, updateChange);
    } else {
      replaceChangeInNode(tr, target.node, pos, changeId, updateChange);
    }
  });

  tr.setMeta(TrackedChangesIgnoreMetaKey, true);

  return tr;
}

/**
 * Collects the distinct ids of every tracked change in `doc`.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc to search.
 * @returns The distinct tracked change ids found in `doc`.
 */
function collectAllTrackedChangeIds(schema: CollabSchema, doc: ProseMirrorNode): string[] {
  const changeIds = new Set<string>();

  doc.descendants(node => {
    if (node.isText || node.type.spec.unchangeable) {
      node.marks?.forEach(mark => {
        if (mark.type === schema.changeMark) {
          getTrackedChangeIdsFromAttrValue(mark.attrs[ChangeAttrName.ChangeIds] ?? '').forEach(changeId => changeIds.add(changeId));
        }
      });
    } else if (node.attrs?.[ChangeAttrName.ChangeIds]) {
      getTrackedChangeIdsFromAttrValue(node.attrs[ChangeAttrName.ChangeIds]).forEach(changeId => changeIds.add(changeId));
    }
  });

  return Array.from(changeIds);
}

/**
 * Accepts every tracked change in `doc`.
 * @param tr The transaction to apply the acceptance to.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc (equal to `tr.doc` before this call's steps) to resolve every tracked change in.
 * @returns `tr`, for chaining.
 */
export function acceptAllTrackedChanges(tr: Transaction, schema: CollabSchema, doc: ProseMirrorNode): Transaction {
  collectAllTrackedChangeIds(schema, doc).forEach(changeId => acceptTrackedChange(tr, schema, tr.doc, changeId));
  return tr;
}

/**
 * Rejects every tracked change in `doc`.
 * @param tr The transaction to apply the rejection to.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc (equal to `tr.doc` before this call's steps) to resolve every tracked change in.
 * @returns `tr`, for chaining.
 */
export function rejectAllTrackedChanges(tr: Transaction, schema: CollabSchema, doc: ProseMirrorNode): Transaction {
  collectAllTrackedChangeIds(schema, doc).forEach(changeId => rejectTrackedChange(tr, schema, tr.doc, changeId));
  return tr;
}

/**
 * Returns the effective resolve option for a tracked change in a review package apply: the change's own
 * `reviewMergeOption` mark if it has one, else `packageOption`'s default for unmarked changes - `Accept`/
 * `Reject` default unmarked changes to that same resolution, while `Transfer` leaves them unresolved
 * (`undefined`) so they merge as still-pending tracked changes.
 * @param change The tracked change to resolve.
 * @param packageOption The review package's resolve option, used as the default for unmarked changes.
 * @returns The effective option, or `undefined` if the change should be left untouched.
 */
function getEffectiveReviewApplyOption(change: TrackedChange, packageOption: ReviewApplyOption): ReviewApplyOption | undefined {
  return change.reviewMergeOption ?? (packageOption === ReviewApplyOption.Transfer ? undefined : packageOption);
}

/**
 * Resolves every tracked change in `doc` for a review package apply, honoring each change's individual
 * `reviewMergeOption` mark (set via `setTrackedChangeReviewMergeOption`) ahead of the package-level resolve
 * option. A change's own mark always wins; `packageOption` only supplies the default disposition for changes
 * nobody marked - `Accept`/`Reject` default unmarked changes to that same resolution, while `Transfer` leaves
 * unmarked changes untouched (so they land as still-pending tracked changes). Calling this with
 * `packageOption = ReviewApplyOption.Accept` on a doc with no marks produces the same result as
 * `acceptAllTrackedChanges`; likewise for `Reject` and `rejectAllTrackedChanges`.
 * @param tr The transaction to apply the resolutions to.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc (equal to `tr.doc` before this call's steps) to resolve every tracked change in.
 * @param packageOption The review package's resolve option, used as the default for unmarked changes.
 * @returns `tr`, for chaining.
 */
export function resolveTrackedChangesForReviewApply(tr: Transaction, schema: CollabSchema, doc: ProseMirrorNode, packageOption: ReviewApplyOption): Transaction {
  collectAllTrackedChangeIds(schema, doc).forEach(changeId => {
    const change = findTrackedChangeTargets(schema, tr.doc, changeId)[0]?.change;
    if (!change) {
      return;
    }

    const effectiveOption = getEffectiveReviewApplyOption(change, packageOption);

    if (effectiveOption === ReviewApplyOption.Accept) {
      acceptTrackedChange(tr, schema, tr.doc, changeId);
    } else if (effectiveOption === ReviewApplyOption.Reject) {
      rejectTrackedChange(tr, schema, tr.doc, changeId);
    }
    // Else the change is left untouched - it lands as a still-pending tracked change in the merged doc.
  });

  return tr;
}

/**
 * Returns whether applying `packageOption` to `doc` would result in no content changing in the workspace -
 * either because `doc` has no tracked changes at all, or because every tracked change's effective resolution
 * is `Reject`. Used to skip an otherwise no-op merge in
 * `DocsMultiStore.applyReviewFileToWorkspaceAsync`. `Array.prototype.every` is true on an empty
 * array, so a doc with no tracked changes at all is correctly reported as "all rejected" without a separate check.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc to check.
 * @param packageOption The review package's resolve option, used as the default for unmarked changes.
 * @returns Whether every tracked change in `doc` (if any) resolves to `Reject`.
 */
export function allTrackedChangesRejectedForReviewApply(schema: CollabSchema, doc: ProseMirrorNode, packageOption: ReviewApplyOption): boolean {
  return collectAllTrackedChangeIds(schema, doc).every(changeId => {
    const change = findTrackedChangeTargets(schema, doc, changeId)[0]?.change;
    if (!change) {
      return true;
    }

    return getEffectiveReviewApplyOption(change, packageOption) === ReviewApplyOption.Reject;
  });
}

/**
 * Checks whether the tracked changes in `doc` that have no accept/reject `reviewMergeOption` set, returning when the first resolved track change is found.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc to search.
 * @returns If there are unresolved tracked changes in `doc`.
 */
export function docHasUnresolvedTrackedChanges(schema: CollabSchema, doc: ProseMirrorNode): boolean {
  for (const changeId of collectAllTrackedChangeIds(schema, doc)) {
    const change = findTrackedChangeTargets(schema, doc, changeId)[0]?.change;
    if (change && change.reviewMergeOption == undefined) {
      return true;
    }
  }
  return false;
}

/**
 * Counts, for each of the tracked changes in `doc`, how many have an accept/reject `reviewMergeOption` set
 * against the total number of tracked changes.
 * @param schema The schema `doc` belongs to.
 * @param doc The doc to search.
 * @returns The doc's resolved and total tracked change counts.
 */
export function countResolvedTrackedChanges(schema: CollabSchema, doc: ProseMirrorNode): ReviewFilesTrackedChangeCount {
  let count = 0;
  let totalCount = 0;

  for (const changeId of collectAllTrackedChangeIds(schema, doc)) {
    const change = findTrackedChangeTargets(schema, doc, changeId)[0]?.change;
    if (change && (change.reviewMergeOption == ReviewApplyOption.Accept || change.reviewMergeOption == ReviewApplyOption.Reject)) {
      count++;
    }
    totalCount++;
  }

  return { resolvedChanges: count, totalChanges: totalCount };
}
